import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * The <source> elements ship with `data-video-source` instead of `src`, so the
 * browser downloads nothing until this component assigns the real URLs. We hold
 * that assignment back until the component is about to enter the viewport, which
 * keeps below-the-fold background videos out of the initial page load.
 *
 * The poster image sits behind the <video> element, so the video is kept hidden
 * until it has a first frame to paint. Otherwise an empty video element can cover
 * the poster while it buffers.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    // Older browsers without IntersectionObserver keep the previous eager behaviour.
    if (typeof IntersectionObserver === 'undefined') {
      this.#loadVideo();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          this.#observer?.disconnect();
          this.#observer = null;
          this.#loadVideo();
          return;
        }
      },
      { rootMargin: '800px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = null;
  }

  #loadVideo() {
    if (this.dataset.videoLoaded) return;
    this.dataset.videoLoaded = 'true';

    const { videoSources, videoElement } = this.refs;

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      videoElement.style.removeProperty('visibility');
    };

    // Keep the poster image visible until the video has something to show.
    videoElement.style.visibility = 'hidden';
    videoElement.addEventListener('loadeddata', reveal, { once: true });
    videoElement.addEventListener('error', reveal, { once: true });
    setTimeout(reveal, 4000);

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
